/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import Table, { TableColumn } from '../../components/table/Table';
import { Button, Input } from 'reactstrap';
import SimpleTooltip from '../../components/simple-tooltip';
import { DATE, formatDate } from '../../utils/date';
import { companyTrackerUserRoles, portfolioTrackerUserRoles, UserRoles } from '../../constants/user';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { UserFilter } from './UserFilter';
import { useAppSelector } from '../../reducers';
import { escapeRegexCharacters } from '../../utils/string-format';
import { BasicAlert } from '../../components/alert/BasicAlert';
import { UserTableMenuButton } from './partials/UserTableMenuButton';
import { UserRolesDropdown } from './partials/UserRolesDropdown';
import { OnboardingModelPlain, OnboardingStatus } from '../../types/onboarding';
import { SafeUser } from '../../constants/users';
import './styles.scss';
import { RequestToJoinModal } from './RequestToJoinModal';
import { SelectOption } from '../../types/select';

export type MgmtUser = Pick<SafeUser,
  '_id' |
  'permissions' |
  'firstName' |
  'surname' |
  'isStaff' |
  'lastLogin' |
  'email'
>;

export type MgmtInvitation = Pick<OnboardingModelPlain, '_id' | 'created' | 'initiativeId' | 'user' | 'status' | 'type'>;

export interface UserTableUser {
  _id: string;
  name?: string;
  email?: string;
  roles: UserRoles[];
  lastUpdated?: string;
  lastLogin?: string;
  isActivated: boolean;
  isRequestToJoin: boolean;
  isInherited: boolean;
  isStaff: boolean;
  user?: MgmtUser;
  invitation?: MgmtInvitation;
}

interface UserTableProps {
  handleReload: () => Promise<void>;
  users: UserTableUser[];
  toggleUserInvitationForm: () => void;
  isAddUserDisabled?: boolean;
  btnText?: string;
};

const getStatus = (user: UserTableUser): string => {
  if (user.isActivated) {
    return 'Active';
  }
  if (user.isRequestToJoin) {
    return 'Not Started';
  }
  return 'Pending';
}

const UserTable = ({ handleReload, users, toggleUserInvitationForm, isAddUserDisabled = false, btnText = 'Add user' }: UserTableProps) => {

  const match = useRouteMatch<{ initiativeId?: string, portfolioId?: string }>();
  const location = useLocation();
  const history = useHistory();

  const initiativeId = match.params.initiativeId ?? match.params.portfolioId ?? '';
  const roles = match.params.portfolioId ? portfolioTrackerUserRoles : companyTrackerUserRoles;

  const [searchText, setSearchText] = useState('');
  const [filteredUsers, setFilteredUsers] = useState<(UserTableUser[])>(users);
  const [requestToJoinModal, setRequestToJoinModal] = useState<MgmtInvitation | undefined>(undefined);

  const [filterStatus, setFilterStatus] = useState<'active' | 'pending' | undefined>();
  const [filterPermission, setFilterPermission] = useState<UserRoles | undefined>();
  const currentUserState = useAppSelector((state) => state.currentUser);

  const hasOwner = users.some(user => user.roles.includes(UserRoles.Owner));

  useEffect(() => {
    if (users.length > 0 && location.search) {
      const searchParams = new URLSearchParams(location.search);
      const id = searchParams.get('onboardingId');
      if (id) {
        const user = users.find(u => u.invitation?._id === id);
        if (user?.invitation && user.invitation.status === OnboardingStatus.NotStarted) {
          setRequestToJoinModal(user.invitation)
        }
        searchParams.delete('onboardingId');
        history.replace({ pathname: location.pathname, search: searchParams.toString() });
      }
    }
  }, [history, location.pathname, location.search, users]);

  React.useEffect(() => {
    const filterUsers = (user: UserTableUser) => {
      if (filterStatus) {
        if (filterStatus === 'active' && !user.isActivated) {
          return false;
        }
        if (filterStatus === 'pending' && user.isActivated) {
          return false;
        }
      }

      if (filterPermission) {
        if (!user.roles.includes(filterPermission as UserRoles)) {
          return false;
        }
      }

      if (searchText) {
        const escapedSearchText = escapeRegexCharacters(searchText.trim().toLowerCase());
        const searchTextRegex = new RegExp(escapedSearchText.replace(/ /g, '|'), 'g');
        if ((`${user.name}`.toLowerCase().match(searchTextRegex) || []).length > 0) {
          return true;
        }
        if ((`${user.email}`.toLowerCase().match(searchTextRegex) || []).length > 0) {
          return true;
        }
        return false;
      }
      return true;
    };
    setFilteredUsers(users.filter(filterUsers));
  }, [users, initiativeId, searchText, filterStatus, filterPermission]);

  const handlePermissionChange = (filter: SelectOption<UserRoles | undefined> | SelectOption<UserRoles | undefined>[]) => {
    if (Array.isArray(filter)) {
      return;
    }
    setFilterPermission(filter?.value ?? undefined)
  };

  const handleStatusChange = (filter: SelectOption<'active' | 'pending' | undefined> | SelectOption<'active' | 'pending' | undefined>[]) => {
    if (Array.isArray(filter)) {
      return;
    }
    setFilterStatus(filter?.value ?? undefined)
  };

  const isCurrentUserOrInherited = (user: UserTableUser): boolean => {
    const isCurrentUser = user._id === currentUserState.data._id;
    return user.isInherited || isCurrentUser;
  }

  const tableColumns: TableColumn<UserTableUser>[] = [
    {
      accessor: '_id',
      sortable: false,
      Header: () => '',
      cellProps: {
        className: 'text-center'
      },
      Cell: (c) => {
        const user = c.row.original;
        if (user.isInherited) {
          return <SimpleTooltip text='This user has permissions that were inherited from another reporting level. You cannot manage this user from this level.'>
            <i className='fa-light fa fa-circle-info' color='secondary' />
          </SimpleTooltip>
        }

        const isCurrentUser = user._id === currentUserState.data._id;
        if (isCurrentUser) {
          return <SimpleTooltip text='You cannot modify your own permissions.'>
            <i className='fa-light fa fa-circle-info' color='secondary' />
          </SimpleTooltip>
        }
        return <UserTableMenuButton
          hasOwner={hasOwner}
          user={user}
          initiativeId={initiativeId}
          handleReload={handleReload}
        />
      },
    },
    {
      accessor: 'name',
      Header: () => 'Name',
      cellProps: {
        className: 'dont_translate'
      },
      Cell: (c) => {
        const user = c.row.original;
        return (
          <div className={`${isCurrentUserOrInherited(user) ? 'text-ThemeBgDisabled' : ''}`}>
            {user.name}
          </div>
        );
      },
    },
    {
      id: 'email',
      accessor: 'email',
      Header: () => 'Email address',
      cellProps: {
        className: 'emailCol dont_translate'
      },
      Cell: (c) => {
        const user = c.row.original;
        return (
          <SimpleTooltip text={user.email}>
            <div className={`${isCurrentUserOrInherited(user) ? 'text-ThemeBgDisabled' : ''} hover-expand text-truncate`}>
              {user.email}
            </div>
          </SimpleTooltip>
        );
      },
    },
    {
      id: 'roles',
      accessor: 'roles',
      Header: () => 'Permissions',
      Cell: (c) => {
        const user = c.row.original;
        if (!currentUserState.loaded) {
          return;
        }

        return <UserRolesDropdown
          currentUser={currentUserState.data}
          user={user}
          initiativeId={initiativeId}
          roles={roles}
          handleReload={handleReload}
        />
      }
    },
    {
      id: 'lastLogin',
      accessor: (originalRow) => originalRow.lastLogin ? formatDate(originalRow.lastLogin, DATE.SORTABLE) : null,
      Header: () => 'Last login',
      Cell: (c) => {
        const user = c.row.original;
        return (
          <span className={isCurrentUserOrInherited(user) ? 'text-ThemeBgDisabled' : ''}>
            {user.isRequestToJoin ? undefined : user.lastLogin ? formatDate(user.lastLogin, DATE.HUMANIZE) : 'never'}
          </span>
        );
      },
    },
    {
      id: 'status',
      accessor: (d) => getStatus(d),
      Header: () => 'Status',
      Cell: (c) => {
        const user = c.row.original;
        if (user.isRequestToJoin) {
          return (
            <Button
              className='text-nowrap p-0'
              color='link' size='sm'
              onClick={() => user.invitation && setRequestToJoinModal(user.invitation)}>
              View request
            </Button>
          );
        }

        return (
          <div className={`${isCurrentUserOrInherited(user) ? 'text-ThemeBgDisabled' : ''}`}>
            {c.value}
          </div>
        );
      }
    },
  ];

  const noResults = filteredUsers.length === 0;
  let noUsersMessage = '';
  if (noResults) {
    noUsersMessage =
      filterPermission || filterStatus || searchText
        ? 'No users match your criteria'
        : 'You have no users assigned to this reporting level';
  }

  return (
    <>
      <div className='d-flex align-items-center align-items-stretch' style={{ height: '36px' }}>
        <div>
          <Input
            placeholder={'Search for user...'}
            onChange={(e) => setSearchText(e.target.value)}
            value={searchText} />
        </div>
        <div className='ml-2'>
          <UserFilter
            filterStatus={filterStatus}
            handleStatusChange={handleStatusChange}
            filterPermission={filterPermission}
            handlePermissionChange={handlePermissionChange}
            defaultOptionText='All Users'
          />
        </div>
        <div className='ml-auto'>
          <Button
            outline
            color='primary'
            className={'px-3 py-1 h-auto'}
            onClick={toggleUserInvitationForm}
            disabled={isAddUserDisabled}
          >
            <i className={'fa fa-plus mr-2'} />
            <span>{btnText}</span>
          </Button>
        </div>
      </div>

      <div className='mt-3 user-table-container'>
        <Table
          columns={tableColumns}
          data={filteredUsers}
          pageSize={10}
        />
        {noResults ? <BasicAlert type='secondary' className='mt-2'>{noUsersMessage}</BasicAlert> : null}
      </div>

      {requestToJoinModal ? <RequestToJoinModal invitation={requestToJoinModal} handleReload={handleReload} toggle={() => setRequestToJoinModal(undefined)} /> : null}
    </>
  );
}

export default UserTable;
